import { useCallback } from 'react';
import { useGetPayoutAccountInfoQuery } from '@graphql/queries/registry.graphql.generated';
import { hasActiveStripeAccount } from '@shared/utils/payoutAccount';
import type { RegistryCurrencyLock, RegistryCurrencyLockOptions } from './useRegistryCurrencyLock.types';

/** The one rule for whether a couple may change the registry currency: not once a payout account exists. */
export const useRegistryCurrencyLock = (eventId: Maybe<string>, { skip = false }: RegistryCurrencyLockOptions = {}): RegistryCurrencyLock => {
  const isSkipped = skip || !eventId;
  const { data, loading, error, refetch } = useGetPayoutAccountInfoQuery({
    batchMode: 'fast',
    // cache-and-network: a cached "no account" can be stale, e.g. from another tab; read again on mount.
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    skip: isSkipped,
    variables: { eventId: eventId! },
  });

  const retry = useCallback(() => {
    refetch?.().catch(() => undefined);
  }, [refetch]);

  const hasError = !isSkipped && !!error;
  const isLoading = !isSkipped && loading;

  if (isSkipped || isLoading || hasError) {
    return { isCurrencyLocked: true, hasPayoutAccountLock: false, lockedCurrencyCode: null, isLoading, hasError, retry };
  }

  const payoutAccount = data?.getRegistryEventData?.payoutAccount;
  const hasPayoutAccountLock = !!payoutAccount?.status && hasActiveStripeAccount(payoutAccount.status);

  return {
    isCurrencyLocked: hasPayoutAccountLock,
    hasPayoutAccountLock,
    lockedCurrencyCode: hasPayoutAccountLock ? (payoutAccount?.currencyCode ?? null) : null,
    isLoading,
    hasError,
    retry,
  };
};
